import type { AxiosRequestConfig } from "axios";
import { api, CustomAxiosRequestConfig, SILENT_CONFIG } from "@/shared/api/client";
import {
    LoginDTO,
    LoginResponse,
    InvitationDTO,
    AcceptInvitationRequest,
    MyTenant,
    CurrentTenantDTO,
    PublicTenantBrandingDTO,
    SwitchTenantRequest,
    ValidateOTPRequest,
    AuthResponse,
    ForgotPasswordRequest,
    ForgotPasswordResponse,
    ResetPasswordRequest
} from "@/shared/types/auth";

const WEB_AUTH_CONFIG: AxiosRequestConfig & Pick<CustomAxiosRequestConfig, "skipAuth"> = {
    skipAuth: true,
    headers: { "X-App-Client": "web" },
};

// As telas públicas mostram o erro no próprio estado; o toast global duplicaria a mensagem.
const PUBLIC_SILENT_CONFIG = { skipAuth: true, skipErrorToast: true } as CustomAxiosRequestConfig;

export const authService = {
    login: async (data: LoginDTO): Promise<LoginResponse> => {
        const response = await api.post<LoginResponse>("/auth/login", data, WEB_AUTH_CONFIG);
        return response.data;
    },

    validateOTP: async (data: ValidateOTPRequest): Promise<AuthResponse> => {
        const response = await api.post<AuthResponse>("/auth/validate", data, WEB_AUTH_CONFIG);
        return response.data;
    },

    refreshToken: async (refreshToken?: string): Promise<AuthResponse> => {
        const payload = refreshToken ? { refreshToken } : {};
        const response = await api.post<AuthResponse>("/auth/refresh", payload, WEB_AUTH_CONFIG);
        return response.data;
    },

    logout: async (refreshToken?: string): Promise<void> => {
        const payload = refreshToken ? { refreshToken } : {};
        await api.post("/auth/logout", payload);
    },

    forgotPassword: async (data: ForgotPasswordRequest): Promise<ForgotPasswordResponse> => {
        const response = await api.post<ForgotPasswordResponse>("/auth/forgot-password", data, { skipAuth: true } as CustomAxiosRequestConfig);
        return response.data;
    },

    /** Responde 204 e fecha as sessões da pessoa em todas as organizações. */
    resetPassword: async (data: ResetPasswordRequest): Promise<void> => {
        await api.post("/auth/reset-password", data, { skipAuth: true } as CustomAxiosRequestConfig);
    },

    getInvitation: async (token: string): Promise<InvitationDTO> => {
        const response = await api.get<InvitationDTO>(`/auth/invitations/${encodeURIComponent(token)}`, PUBLIC_SILENT_CONFIG);
        return response.data;
    },

    acceptInvitation: async (token: string, data: AcceptInvitationRequest): Promise<void> => {
        await api.post(`/auth/invitations/${encodeURIComponent(token)}/accept`, data, PUBLIC_SILENT_CONFIG);
    },

    getCurrentTenant: async (): Promise<CurrentTenantDTO> => {
        // Sem toast: sem marca a app segue com a da 7Days.
        const response = await api.get<CurrentTenantDTO>("/auth/me/tenant", SILENT_CONFIG);
        return response.data;
    },

    /** Marca antes do login; 404 quando o slug não é de nenhuma organização ativa. */
    getTenantBySlug: async (slug: string): Promise<PublicTenantBrandingDTO> => {
        const response = await api.get<PublicTenantBrandingDTO>(`/auth/tenants/by-slug/${encodeURIComponent(slug)}`, PUBLIC_SILENT_CONFIG);
        return response.data;
    },

    getMyTenants: async (): Promise<MyTenant[]> => {
        // Sem toast: quem não pode listar organizações só não vê o seletor.
        const response = await api.get<MyTenant[]>("/auth/me/tenants", SILENT_CONFIG);
        return response.data;
    },

    /** Emite tokens para outra organização da mesma pessoa, sem senha nem OTP. */
    switchTenant: async (data: SwitchTenantRequest): Promise<AuthResponse> => {
        const response = await api.post<AuthResponse>("/auth/switch-tenant", data, {
            headers: WEB_AUTH_CONFIG.headers,
            skipErrorToast: true,
        } as CustomAxiosRequestConfig);
        return response.data;
    },

};
